import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Stack } from 'expo-router';
import { NativeTabs } from 'expo-router/unstable-native-tabs';
import { Platform } from 'react-native';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { NAV_LABELS } from '@/constants/navigation';
import { BASE_STACK_SCREEN_OPTIONS } from '@/constants/screenOptions';
import { useGroupedInquiriesEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useColors } from '@/hooks/useColors';
import { useHaptic } from '@/hooks/useHaptic';
import { useUnreadCounts } from '@/hooks/useUnreadCounts';
import { segmentClient } from '@/util/analytics';

/**
 * Tab section layout (secure group)
 * ---------------------------------
 * Declares every route under `(tabs)` and states, per screen, who has one. Access is
 * declared rather than enforced by a redirect: a guarded screen's name is dropped from the
 * navigator, so there is no route to be on, nothing to detect, and nobody to chase off one.
 *
 * Three invariants hold it together:
 *
 * - **Every screen is declared, on both platforms.** Routes come from the filesystem, not
 *   from these declarations, so a guard has no effect on a screen it does not name and an
 *   undeclared screen is a reachable one. Web used to render a bare `Slot` here, which
 *   declares nothing and so could carry no guard at all; it renders its own `Stack` now for
 *   that reason. The two lists are written twice and a test holds them to each other.
 *
 * - **Declaration order carries two separate jobs, so it is load-bearing twice over.** It
 *   is the tab bar order, which is why a guard wraps a screen where it already sits rather
 *   than gathering the guarded ones together. It is also where a guarded-out reader lands:
 *   closing the guard on the focused screen sends them to the first *surviving declared*
 *   screen, so `directory` is written first deliberately and must stay both first and
 *   unguarded. A test pins that.
 *
 *   There is no `unstable_settings.anchor` here on purpose. It looks like the way to name
 *   that destination and is not: it feeds the linking config and child sorting, never the
 *   navigator's `initialRouteName`, so the fallback stays the first surviving declaration
 *   whatever it says. Measured, setting an anchor did one real thing — it prefixed the
 *   parsed state for a directly loaded tab route, mounting the anchor screen underneath
 *   every such load and running its queries. Do not reintroduce it to document intent.
 *
 * - **A guard stays open while its answer is unknown.** See `mayUseVerifiedRoutes` below.
 *
 * Native renders `NativeTabs`, which has no `Protected` and treats a hidden or undeclared
 * trigger as unreachable, so a guard there is the trigger's `hidden` prop. `invites`, `login`
 * and `content-library` are therefore always hidden on native.
 *
 * This is client-side reachability, not authorization. The secure layout above still
 * redirects, and the API stays the enforcement boundary.
 */
export default function TabsLayout() {
  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const { needsNpiVerification, isUnauthenticated } = useAuthStatus();

  /**
   * Open while the current-user query is still in flight, not closed.
   *
   * The obvious spelling is a direct `ability.can(...)` check, and it differs from this one
   * only during that in-flight window — which is exactly where it would do damage. It reads
   * false before the answer arrives, so the guard would close, empty the navigator of these
   * three screens and bounce the reader to the anchor on the strength of data nobody has
   * yet. `needsNpiVerification` reads false in the same window, holding the routes open
   * until the answer settles.
   *
   * Staying open on *unknown* is safe here because this guard is reachability hygiene
   * layered over two real boundaries: the secure layout renders nothing at all while the
   * query loads, so these screens are unreachable during the window regardless, and the API
   * refuses on its own account. The same lesson is written down for an unsettled feature
   * flag on the Fellows Corner route — treating "not yet known" as "no" turns a slow
   * response into a wrong answer for the very people the rule meant to admit.
   */
  const mayUseVerifiedRoutes = !needsNpiVerification;

  const { colors } = useColors();

  const { hasAnyUnread: hasUnreadMessages } = useUnreadCounts();

  const haptic = useHaptic();

  const inboxBadgeNativeProps =
    Platform.OS === 'android' && hasUnreadMessages ? { badgeValue: '' } : undefined;

  /*
   * Web has no tab bar — `NavBar` in the secure layout is its navigation — so this renders
   * the plain stack the tab bar would otherwise sit under. It is a `Stack` rather than the
   * `Slot` it used to be for one reason only: a `Slot` declares no screens, so it can carry
   * no guard.
   *
   * That swap is not free, and the cost is worth knowing before anyone tunes this. `Slot`
   * rendered only the focused route. A `Stack` keeps the routes below the top mounted, and
   * a nav link dispatches `NAVIGATE` rather than replacing, so walking between tabs
   * accumulates them and their queries and non-focus-scoped effects stay alive behind the
   * screen on top. Measured on web, the first-visited tab is still in the DOM two
   * navigations later. A bar-hidden `Tabs` would unify the two branches and retains in much
   * the same way, so retention is not the reason to prefer this one.
   */
  if (isWeb) {
    return (
      <Stack screenOptions={{ ...BASE_STACK_SCREEN_OPTIONS, animation: 'none' }}>
        <Stack.Screen name="(directory)" />

        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen name="(inbox)" />
        </Stack.Protected>

        <Stack.Screen name="(inquiry)" />

        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen name="invites" />
        </Stack.Protected>

        <Stack.Screen name="(community)" />

        <Stack.Protected guard={isUnauthenticated}>
          <Stack.Screen name="login" />
        </Stack.Protected>

        <Stack.Screen name="content-library" />
      </Stack>
    );
  }

  return (
    <NativeTabs
      tintColor={colors.primary[800]}
      badgeBackgroundColor={colors.tertiary[400]}
      backgroundColor={colors.background[0]}
      indicatorColor={colors.primary[100]}
      labelVisibilityMode="labeled"
      minimizeBehavior="never"
      iconColor={{ default: colors.typography[500], selected: colors.primary[800] }}
      labelStyle={{
        default: { color: colors.typography[600] },
        selected: { color: colors.primary[800] },
      }}>
      <NativeTabs.Trigger
        name="(directory)"
        disableScrollToTop
        listeners={{
          tabPress: () => {
            haptic.impactLight();

            segmentClient.track(ANALYTICS_EVENTS.DIRECTORY_NAV_ITEM_CLICKED);
          },
        }}>
        <NativeTabs.Trigger.Label>{NAV_LABELS.directory.mobileTab}</NativeTabs.Trigger.Label>
        <NativeTabs.Trigger.Icon sf="magnifyingglass" md="search" />
      </NativeTabs.Trigger>

      <NativeTabs.Trigger
        name="(inbox)"
        disableScrollToTop
        hidden={!mayUseVerifiedRoutes}
        unstable_nativeProps={inboxBadgeNativeProps}
        listeners={{
          tabPress: () => {
            haptic.impactLight();

            segmentClient.track(ANALYTICS_EVENTS.INBOX_VIEWED, {
              source: 'tab',
              inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
            });
          },
        }}>
        <NativeTabs.Trigger.Label>{NAV_LABELS.inbox.mobileTab}</NativeTabs.Trigger.Label>
        <NativeTabs.Trigger.Icon sf="tray" md="inbox" />
        {/* No children: the badge ignores `hidden` once it has any, a lone space included. */}
        {Platform.OS === 'ios' ? <NativeTabs.Trigger.Badge hidden={!hasUnreadMessages} /> : null}
      </NativeTabs.Trigger>

      <NativeTabs.Trigger
        name="(inquiry)"
        listeners={{
          tabPress: () => {
            haptic.impactLight();

            segmentClient.track(ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED, {
              source: 'mobile_tab',
            });
          },
        }}>
        <NativeTabs.Trigger.Label>{NAV_LABELS.inquiry.mobileTab}</NativeTabs.Trigger.Label>
        <NativeTabs.Trigger.Icon sf="square.and.pencil" md="edit_square" />
      </NativeTabs.Trigger>

      <NativeTabs.Trigger name="invites" hidden />

      <NativeTabs.Trigger
        name="(community)"
        disableScrollToTop
        listeners={{
          tabPress: () => {
            haptic.impactLight();

            segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_CLICKED);
          },
        }}>
        <NativeTabs.Trigger.Label>{NAV_LABELS.community.mobileTab}</NativeTabs.Trigger.Label>
        <NativeTabs.Trigger.Icon sf="book" md="menu_book" />
      </NativeTabs.Trigger>

      <NativeTabs.Trigger name="login" hidden />

      <NativeTabs.Trigger name="content-library" hidden />
    </NativeTabs>
  );
}
